import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { faPen, faTrash, faTrashArrowUp } from '@fortawesome/pro-light-svg-icons';
import { Actions, ofType } from '@ngrx/effects';
import { CellClickEvent, GridComponent } from '@progress/kendo-angular-grid';
import { cloneDeep } from 'lodash-es';
import { map, take } from 'rxjs/operators';
import { ShiveRoutes } from 'src/app/core/routing/segments/route-segments';
import { SubSink } from 'subsink';
import { listAnimation } from '../../../core/animations/animations';
import { ProjectModel } from '../../../core/models/project/project.model';
import { ProjectsHttpService } from '../../../core/state/projects/projects-http.service';
import * as fromProjectActions from '../../../core/state/projects/projects.actions';
import { ShiveGridWrapperComponent } from '../../../shared/components/shive-controls/shive-grid/shive-grid-wrapper.component';
import { DropdownOption } from '../../../shared/data-types/dropdown-option';
import { ProjectStatusFilter } from '../shared/project-detail-types';

@Component({
  selector: 'app-project-list',
  templateUrl: './project-list.component.html',
  styleUrls: ['./project-list.component.scss'],
  animations: [listAnimation],
  standalone: false,
})
export class ProjectListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('grid') grid: GridComponent;
  @ViewChild('gridWrapperComponent') gridWrapperComponent: ShiveGridWrapperComponent;
  public ShiveRoutes = ShiveRoutes;
  public gridData: ProjectModel[] = [];
  public projectStatusFilter: readonly DropdownOption[] = [
    { text: 'Alle', value: ProjectStatusFilter.All },
    { text: 'Aktiv', value: ProjectStatusFilter.Active },
    { text: 'Archiviert', value: ProjectStatusFilter.Archived },
  ];
  public readonly ProjectStatusFilter = ProjectStatusFilter;
  private readonly actions$ = inject(Actions);
  private currentFilter: ProjectStatusFilter = undefined;
  private readonly subs = new SubSink();

  constructor(
    private readonly projectsHttpService: ProjectsHttpService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.fetchProjects();
    this.httpUpdateCallback();
  }

  ngAfterViewInit(): void {
    this.initContextMenuCallbacks();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  public onCellClick(e: CellClickEvent): void {
    if (e.columnIndex === this.grid.columns.length - 1) {
      return;
    }
    const projectModel = e.dataItem as ProjectModel;
    void this.router.navigate(['../', ShiveRoutes.Detail, ShiveRoutes.Progress, projectModel.id], {
      relativeTo: this.route,
    });
  }

  public projectStatusFilterChanged(filterOption: DropdownOption) {
    this.currentFilter = filterOption.value as ProjectStatusFilter;
    this.fetchProjects();
  }

  private initContextMenuCallbacks(): void {
    this.gridWrapperComponent.contextMenuItems = [
      {
        text: 'Bearbeiten',
        faIcon: faPen,
        callback: () => {},
        isVisible: (projectModel: ProjectModel) => !projectModel.isArchived,
        isActivated: true,
      },
      {
        text: 'Archivieren',
        faIcon: faTrash,
        callback: (projectModel: ProjectModel) => {
          const updatedProjectModel = cloneDeep(projectModel);
          updatedProjectModel.isArchived = true;
          this.projectsHttpService.updateProject(updatedProjectModel);
        },
        isVisible: (projectModel: ProjectModel) => !projectModel.isArchived,
        isActivated: true,
      },
      {
        text: 'Wiederherstellen',
        faIcon: faTrashArrowUp,
        callback: (projectModel: ProjectModel) => {
          const updatedProjectModel = cloneDeep(projectModel);
          updatedProjectModel.isArchived = false;
          this.projectsHttpService.updateProject(updatedProjectModel);
        },
        isVisible: (projectModel: ProjectModel) => projectModel.isArchived,
        isActivated: (projectModel: ProjectModel) => {
          return !projectModel.client.isArchived;
        },
      },
    ];
  }

  private fetchProjects() {
    this.projectsHttpService
      .getProjects(undefined, this.currentFilter)
      .pipe(take(1))
      .subscribe((projects) => (this.gridData = projects));
  }

  private httpUpdateCallback(): void {
    this.subs.sink = this.actions$
      .pipe(
        ofType(fromProjectActions.projectUpdated),
        map((entry) => entry.payload[0]),
      )
      .subscribe(() => {
        this.fetchProjects();
      });
  }
}
